import dayjs from "dayjs";

import { INDEPENDENT_STATION_BOUND_GAP_MINUTES } from "../../constants";
import { IRideDto, IRideMetadata, IStationInfo } from "../../dto";
import { TimeZoneEnum } from "../../enums";
import { RideFormFields, TRideDtoStartEndKey } from "../../models";
import {
  addMinutes,
  dateTimeWithoutSeconds,
  isValidDateTime,
  timeDiffMinutesSigned,
} from "../date.helpers";

const MIN_DRIFT_DAYS_TO_REBASE = 2;

export const shiftStationsArrivalTimesByMinutes = (
  stations: IStationInfo[] | undefined,
  deltaMinutes: number,
): IStationInfo[] => {
  if (!stations?.length || !deltaMinutes) {
    return stations || [];
  }

  return stations.map(station => {
    if (!station.arrivalTime || !isValidDateTime(station.arrivalTime)) {
      return station;
    }

    return {
      ...station,
      arrivalTime: addMinutes(
        dateTimeWithoutSeconds(station.arrivalTime),
        deltaMinutes,
      ),
    };
  });
};

export const shiftRideMetadataStationsByMinutes = ({
  metadata,
  deltaMinutes,
}: {
  metadata?: IRideMetadata;
  deltaMinutes: number;
}): IRideMetadata => {
  if (!metadata?.stationsInfo?.length || !deltaMinutes) {
    return metadata || {};
  }

  return {
    ...metadata,
    stationsInfo: shiftStationsArrivalTimesByMinutes(
      metadata.stationsInfo,
      deltaMinutes,
    ),
  };
};

export const rebaseStationsArrivalTimesToUtcDate = ({
  stations,
  targetDate,
  timezone = TimeZoneEnum.AsiaJerusalem,
}: {
  stations: IStationInfo[] | undefined;
  targetDate: TDateISO | undefined;
  timezone?: TimeZoneEnum;
}): IStationInfo[] => {
  if (!stations?.length || !targetDate || !isValidDateTime(targetDate)) {
    return stations || [];
  }

  const firstArrival = stations.find(
    station => station.arrivalTime && isValidDateTime(station.arrivalTime),
  )?.arrivalTime;

  if (!firstArrival) {
    return stations;
  }

  const targetDay = dayjs.utc(targetDate).tz(timezone).startOf("day");
  const firstArrivalDay = dayjs.utc(firstArrival).tz(timezone).startOf("day");
  const dayDiff = targetDay.diff(firstArrivalDay, "day");

  if (Math.abs(dayDiff) < MIN_DRIFT_DAYS_TO_REBASE) {
    return stations;
  }

  return shiftStationsArrivalTimesByMinutes(
    stations,
    targetDay.diff(firstArrivalDay, "minute"),
  );
};

const isArrivalBeforeRideStart = ({
  arrivalTime,
  rideStart,
}: {
  arrivalTime: TDateISO;
  rideStart: TDateISO;
}): boolean => dayjs.utc(arrivalTime).isBefore(dayjs.utc(rideStart));

const isArrivalAfterRideEnd = ({
  arrivalTime,
  rideEnd,
}: {
  arrivalTime: TDateISO;
  rideEnd: TDateISO;
}): boolean => dayjs.utc(arrivalTime).isAfter(dayjs.utc(rideEnd));

const packTimedStationsFromBound = ({
  stations,
  anchor,
  gapMinutes,
  isFromStart,
}: {
  stations: IStationInfo[];
  anchor: TDateISO;
  gapMinutes: number;
  isFromStart: boolean;
}): IStationInfo[] => {
  const packedStations = stations.map(station => ({ ...station }));
  // Untimed rows stay put; from the end we walk last→first so order survives 1-minute rounding.
  const timedIndexes = packedStations.flatMap((station, index) =>
    station.arrivalTime && isValidDateTime(station.arrivalTime) ? [index] : [],
  );
  const orderedIndexes = isFromStart
    ? timedIndexes
    : [...timedIndexes].reverse();
  let packedAt = addMinutes(anchor, isFromStart ? gapMinutes : -gapMinutes);

  for (const index of orderedIndexes) {
    const station = packedStations[index];
    if (!station || !packedAt) {
      continue;
    }

    packedStations[index] = { ...station, arrivalTime: packedAt };
    packedAt = addMinutes(packedAt, isFromStart ? gapMinutes : -gapMinutes);
  }

  return packedStations;
};

/**
 * Keep in sync with backend `realignIndependentStationsInsideRideWindow`.
 * Past the end → pack toward the end. Before the start (including when the end-pack crosses it) → pack from the start.
 */
export const realignIndependentStationsInsideRideWindow = ({
  stations,
  rideStart,
  rideEnd,
  gapMinutes = INDEPENDENT_STATION_BOUND_GAP_MINUTES,
}: {
  stations: IStationInfo[] | undefined;
  rideStart: TDateISO | undefined;
  rideEnd: TDateISO | undefined;
  gapMinutes?: number;
}): IStationInfo[] | undefined => {
  if (
    !stations?.length ||
    !rideStart ||
    !rideEnd ||
    !isValidDateTime(rideStart) ||
    !isValidDateTime(rideEnd)
  ) {
    return stations;
  }

  const isAfterRideEnd = stations.some(
    station =>
      station.arrivalTime &&
      isValidDateTime(station.arrivalTime) &&
      isArrivalAfterRideEnd({ arrivalTime: station.arrivalTime, rideEnd }),
  );
  const isBeforeRideStart = stations.some(
    station =>
      station.arrivalTime &&
      isValidDateTime(station.arrivalTime) &&
      isArrivalBeforeRideStart({
        arrivalTime: station.arrivalTime,
        rideStart,
      }),
  );

  if (!isAfterRideEnd && !isBeforeRideStart) {
    return stations;
  }

  let realignedStations = stations;
  // Keep in sync with backend: past the end → pack toward end first; a short window can then land before start.
  if (isAfterRideEnd) {
    realignedStations = packTimedStationsFromBound({
      stations: realignedStations,
      anchor: rideEnd,
      gapMinutes,
      isFromStart: false,
    });
  }

  const isPackedBeforeRideStart = realignedStations.some(
    station =>
      station.arrivalTime &&
      isValidDateTime(station.arrivalTime) &&
      isArrivalBeforeRideStart({
        arrivalTime: station.arrivalTime,
        rideStart,
      }),
  );

  if (isBeforeRideStart || isPackedBeforeRideStart) {
    // Original times before start, or the end-pack crossed it → pack forward from start.
    realignedStations = packTimedStationsFromBound({
      stations: realignedStations,
      anchor: rideStart,
      gapMinutes,
      isFromStart: true,
    });
  }

  return realignedStations;
};

export const applyIndependentRideDateChange = ({
  rideDto,
  updatedRideDateTime,
  rideStartEndKey,
}: {
  rideDto: Partial<IRideDto>;
  updatedRideDateTime: TDateISO;
  rideStartEndKey: TRideDtoStartEndKey;
}): {
  rideStart?: TDateISO;
  rideEnd?: TDateISO;
  metadata: IRideMetadata;
} => {
  const isStart = rideStartEndKey === RideFormFields.rideStart;
  const previousValue = rideDto[rideStartEndKey];
  const deltaMinutes =
    previousValue &&
    isValidDateTime(previousValue) &&
    isValidDateTime(updatedRideDateTime)
      ? timeDiffMinutesSigned(
          dateTimeWithoutSeconds(previousValue),
          dateTimeWithoutSeconds(updatedRideDateTime),
        )
      : 0;

  const hasOverlap = isStart
    ? Boolean(
        rideDto.rideEnd &&
        dayjs.utc(rideDto.rideEnd).isBefore(dayjs.utc(updatedRideDateTime)),
      )
    : Boolean(
        rideDto.rideStart &&
        dayjs.utc(updatedRideDateTime).isBefore(dayjs.utc(rideDto.rideStart)),
      );

  const shiftedMetadata = shiftRideMetadataStationsByMinutes({
    metadata: {
      ...(rideDto.metadata || {}),
      hasIndependentDayAdjustment: true,
    },
    deltaMinutes: isStart ? deltaMinutes : 0,
  });
  let resolvedRideStart = rideDto.rideStart;
  let resolvedRideEnd = rideDto.rideEnd;
  if (isStart) {
    resolvedRideStart = updatedRideDateTime;
    if (hasOverlap) {
      resolvedRideEnd = updatedRideDateTime;
    }
  } else {
    resolvedRideEnd = updatedRideDateTime;
    if (hasOverlap) {
      resolvedRideStart = updatedRideDateTime;
    }
  }
  const stationsInfo = realignIndependentStationsInsideRideWindow({
    stations: shiftedMetadata.stationsInfo,
    rideStart: resolvedRideStart,
    rideEnd: resolvedRideEnd,
  });
  const metadata: IRideMetadata = {
    ...shiftedMetadata,
    ...(stationsInfo ? { stationsInfo } : {}),
  };

  if (isStart) {
    return {
      rideStart: updatedRideDateTime,
      ...(hasOverlap ? { rideEnd: updatedRideDateTime } : {}),
      metadata,
    };
  }

  return {
    rideEnd: updatedRideDateTime,
    ...(hasOverlap ? { rideStart: updatedRideDateTime } : {}),
    metadata,
  };
};
